import moment from 'moment'
import React, { ReactNode } from 'react'
import { MapContainer } from '../../containers'
import { images } from '../../images'
import { IAvailability, ICoordinate, IPaymentStatus, IReservation, IStop, ITicketingType } from '../../models'
import { LiteralsService } from '../../services'
import StyledSection from './styles'
import { Stops } from './Stops'
import { IExpeditionStatus } from '../../models/expedition-status'
import 'moment/locale/ca'
import 'moment/locale/es'
import 'moment/locale/en-gb'
import { appsStyleSelected } from '../../helpers/app'
import { TagComponent } from '../ServiceCheckbox/TagComponent'

interface IProps {
  reservation?: IReservation
  availability?: IAvailability
  returnAvailability?: IAvailability
  busLocation: ICoordinate | null
  extraInfoMessage: boolean
  onFavoriteClick?: () => void
  seats?: number
  prmSeats?: number
  aprox?: boolean
  pickedOriginBusStop?: IStop
  pickedDestinationBusStop?: IStop,
  onQRClick?: () => void
  hideMapOnMobile?: boolean
  showRecenterButton?: boolean
}

export const ReservationDetailsCard: React.FC<IProps> = ({
  reservation,
  availability,
  returnAvailability,
  busLocation,
  extraInfoMessage,
  onFavoriteClick,
  seats,
  prmSeats,
  aprox,
  pickedOriginBusStop,
  pickedDestinationBusStop,
  onQRClick,
  hideMapOnMobile,
  showRecenterButton = true
}) => {
  const cancelledBanner = (
    <div className="cancelled-banner banner">
      <h4>{ LiteralsService.get('cancelled', true) }</h4>
      <p>{ LiteralsService.get('reasonsCancelled', true) }</p>
    </div>
  )

  const pendingPaymentBanner = (): React.ReactNode => {
    const ticketingType = reservation?.ticketingType
    return (
      <div className='pending-payment-banner banner'>
        <h4> { LiteralsService.get('pendingPayment', true) } </h4>
        <p>
          {
            ticketingType === ITicketingType.Online ?
              LiteralsService.get('reasonsPendingPaymentMust', true)
            :
              LiteralsService.get('reasonsPendingPayment', true)
          }
        </p>
      </div>
    )
  }

  const pendingRefundBanner = (): React.ReactNode => {
    return (
      <div className='pending-refund-banner banner'>
        <h4>{ LiteralsService.get('pendingRefund', true) }</h4>
        <p>{ LiteralsService.get('reasonsPendingRefund', true) }</p>
      </div>
    )
  }

  let mapDisplay = (<div></div>)

  mapDisplay = (
    <div className="map-container" >
      { reservation?.cancelled && cancelledBanner }
      { ((reservation?.paymentStatus === IPaymentStatus.FAILED || reservation?.paymentStatus === IPaymentStatus.CREATED || (reservation?.ticketingType === ITicketingType.Hybrid && reservation?.paymentStatus === IPaymentStatus.PAY_AFTER)) && reservation?.expeditionStatus !== IExpeditionStatus.Closed) && pendingPaymentBanner() }
      { reservation?.refundId && pendingRefundBanner() }
      <div className="map">
        <MapContainer
          path={ reservation?.path }
          inStop={ reservation?.beginningStop ?? availability?.inStop }
          outStop={ reservation?.seats[0].exitStop ?? availability?.outStops[0] }
          busLocation={ busLocation }
          serviceInfos={ [] }
          correction={ 0.000005 }
          showRecenterButton={ showRecenterButton }
          controlsEnabled
        />
      </div>
    </div>
  )

  const reservationDate = (details: IAvailability | null): string => {
    const locale = LiteralsService.getLanguage()
    moment.locale(locale)
    if (reservation?.beginningStop.dateTime) {
      const time = reservation?.beginningStop.dateTime
      const dt = moment(time).format('DD/MM/YYYY')
      return dt
    } else {
      const time = details?.inStop.dateTime
      const dt = moment(time).format('DD/MM/YYYY')
      return dt
    }
  }

  const reservationDetails = (details: IAvailability | null, isReturn: boolean): ReactNode => {
    const normalSeats =
      reservation && seats === undefined
        ? reservation.seats.filter(s => !s.prm).length
        : seats
    const prioritySeats =
      reservation && prmSeats === undefined
        ? reservation.seats.filter(s => s.prm).length
        : prmSeats
    const totalTickets = Number(normalSeats) + Number(prioritySeats)

    const favoriteButton = onFavoriteClick
    ? (
        <button onClick={ onFavoriteClick }>
          <img
            src={ images[reservation?.favorite ? 'starSelected' : 'star'] }
            alt="Favorite"
          />
        </button>
      )
    : null

    const qrButton = reservation && reservation.qrCode ?
      (
        <button onClick={ onQRClick }>
          <img src={ images['qrCode'] } alt='qr-code' />
        </button>
      )
      : null

      return (
        <section className="detail-row">
          <div className="top-details">
            { stops(details, isReturn) }
            <div className='info-wrapper'>
              <div className='information'>
                <div className='info-container'>
                  <div className='date'>
                    <div>
                      <img src={images.calendar} />
                      <span>
                        { reservationDate(details) }
                      </span>
                    </div>
                  </div>
                  <div className='seats-container'>
                    {
                      normalSeats ?
                      <div>
                        <img src={images.seat} />
                        <span className='seats-text'>
                          <span className='seats-number'>{ normalSeats }</span>
                          { !prioritySeats && <span className='seats-label'> { LiteralsService.get('seat') }</span> }
                        </span>
                      </div> : null
                    }
                    {
                      prioritySeats ?
                      <div>
                        <img src={images.prmSeat} />
                        <span className='seats-text'>
                          <span className='seats-number'>{ prioritySeats }</span>
                          { !normalSeats && <span className='seats-label'> { LiteralsService.get('prmSeat') }</span> }
                        </span>
                      </div> : null
                    }
                  </div>
                  {
                    ((details && details.price) || (reservation && reservation.paymentPrice)) &&
                    <div className='price price-mobile'>
                      <img src={images.price} />
                      <span>{ `${details ? details?.price : reservation?.paymentPrice}€` }</span>
                    </div>
                  }
                </div>
                <div className='info-container info-container-tickets'>
                  <div className='tickets'>
                    <img src={images.ticket} />
                    <span>{ `${totalTickets}x` }</span>
                  </div>
                  {
                    ((details && details.price) || (reservation && reservation.paymentPrice)) &&
                    <div className='price price-desktop'>
                      <img src={images.price} />
                      <span>{ `${details ? details?.price : reservation?.paymentPrice}€` }</span>
                    </div>
                  }
                </div>
              </div>
              <div className='action-buttons'>
                { qrButton }
                { favoriteButton }
              </div>
            </div>
          </div>
        </section>
      )
  }

  const stops = (details: IAvailability | null, isReturn: boolean): ReactNode => {
    const cardReservation = details ? {
      ...details,
      beginningStop: details?.inStop,
      seats: details?.outStops.map(out => ({
        exitStop: out
      })),
      service: {
        id: details.serviceId
      }
    } : reservation

    const directionText = (isReturn: boolean) => {
      if (returnAvailability) {
        if (isReturn) return LiteralsService.get('directions.return').toUpperCase()
        return LiteralsService.get('directions.outbound').toUpperCase()
      }
      return ''
    }

    return (
      <section className='stops'>
        <article className='service-info'>
          <div className='info'>
            <TagComponent tag={cardReservation?.serviceInfo.tag!} color={cardReservation?.serviceInfo.color!} clickable={false} />
            <span>
              { cardReservation?.serviceInfo.name }
            </span>
          </div>
          {
            returnAvailability &&
            <div className='direction-container'>
              <img src={images.directionArrow} className={returnAvailability && isReturn ? 'return' : ''} />
              <span>
                { directionText(isReturn) }
              </span>
            </div>
          }
        </article>
        <Stops
          reservation={ cardReservation as IReservation }
          aprox={ aprox }
          pickedOriginBusStop={ isReturn ? pickedDestinationBusStop : pickedOriginBusStop }
          pickedDestinationBusStop={ isReturn ? pickedOriginBusStop : pickedDestinationBusStop }
        />
      </section>
    )
  }

  const message = (): ReactNode => {
    if (!reservation) return null
    else {
      const { minDateTime, maxDateTime } = reservation.beginningStop
      const minTime = moment(minDateTime)
      const maxTime = moment(maxDateTime)
      const time = moment.duration(maxTime.diff(minTime)).asMinutes()
      const extraInfoText = LiteralsService
        .get('hourMayUpdateWithTime', true)
        .replace('%MINUTESTOREPLACE%', Math.floor(time).toString())
      return !minDateTime || !maxDateTime || !time
        ? null
        : (
            <section className="message">
              <p>{ extraInfoText }</p>
            </section>
          )
    }
  }

  return (
    <StyledSection className={`${appsStyleSelected?.style}${hideMapOnMobile ? ' hide-map-mobile' : ''}${returnAvailability ? ' is-roundtrip' : ''}`}>
      { mapDisplay }
      <section className={`details ${appsStyleSelected?.style}`}>
        <div className={`details-container ${appsStyleSelected?.style}`}>
          <div className='spacer' />
          { reservation && reservationDetails(null, false) }
          { availability && reservationDetails(availability, false) }
          <div className='spacer' />
          { returnAvailability && reservationDetails(returnAvailability, true) }
          <div className='spacer' />
        </div>
        <div>
          { extraInfoMessage && message() }
        </div>
      </section>
    </StyledSection>
  )
}
